import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { Modal } from 'react-daisyui';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context';
import { IsClickModal } from '../../utils/IsClickModal';
import Chat from '../Chat/Chat.component';
import './ChatModal.styles.scss';
import ChatAccessNotice from './ChatAccessNotice.component';
import useChatModalGate from './useChatModalGate.hook';

/**
 * The lead's conversation over whatever page is open (AGI-761). Mounted once,
 * by the private layout, for every route but `/workspace/chat`.
 */
export default function ChatModal() {
  const {
    setSelectedLeadForChatModal,
    setChatModalIsOpen,
    setMaximizeModal,
    chatModalIsOpen,
    maximizeModal,
  } = useModalsContext();
  const gate = useChatModalGate();

  const modalClassName = classNames('ChatModal', {
    'ChatModal--maximized': maximizeModal,
  });

  const clickPosition = useRef({
    x: 0,
    y: 0,
    lastX: 0,
    lastY: 0,
  });

  // Every way out clears the lead too: a lead left selected re-opens the popup
  // when the Pipe or Directory mounts.
  function resetModal() {
    setChatModalIsOpen(false);
    setMaximizeModal(false);
    setSelectedLeadForChatModal(null);
  }

  function handleBackdropClick() {
    // Use the last click registered to verify if it was a drag
    if (IsClickModal(clickPosition.current)) resetModal();
  }

  function handleMouseDown(e: React.MouseEvent) {
    // Save the mouse position when clicked
    clickPosition.current = {
      x: e.clientX,
      y: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
    };
  }

  function handleMouseMove(e: React.MouseEvent) {
    // Update the last known mouse position
    if (clickPosition.current) {
      clickPosition.current.lastX = e.clientX;
      clickPosition.current.lastY = e.clientY;
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // An Esc a popover inside the chat already handled closes that popover, not the chat.
      if (event.key === 'Escape' && !event.defaultPrevented) resetModal();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // The setters are stable; registering once matches the modal's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}>
      <Modal.Legacy
        open={chatModalIsOpen}
        className={modalClassName}
        onClickBackdrop={handleBackdropClick}
      >
        {gate.status === 'ready' ? (
          <Chat isChatModal onCloseModal={resetModal} />
        ) : (
          <ChatAccessNotice status={gate.status} onRetry={gate.retry} onClose={resetModal} />
        )}
      </Modal.Legacy>
    </div>
  );
}
